import { useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import API from "@/lib/api";
import { Input } from "@/components/ui/input";
import { useLanguage } from "@/contexts/LanguageContext";
import { Search, ChevronLeft, ChevronRight, Package } from "lucide-react";

export default function BrandsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [brands, setBrands] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [isFetching, setIsFetching] = useState(false);
  const { t } = useLanguage();

  const searchQuery = searchParams.get("search") || "";
  const currentPage = Math.max(1, Number(searchParams.get("page") || 1));
  const itemsPerPage = 12;

  useEffect(() => {
    const params = new URLSearchParams();
    params.set("limit", String(itemsPerPage));
    params.set("skip", String((currentPage - 1) * itemsPerPage));
    params.set("with_product_counts", "true");
    if (searchQuery.trim()) params.set("search", searchQuery.trim());

    if (brands.length > 0) setIsFetching(true);
    else setLoading(true);

    API.get(`/brands?${params.toString()}`).then((res) => {
      setBrands(res.data.brands || []);
      setTotal(Number(res.data.total || 0));
    }).catch(() => {}).finally(() => {
      setLoading(false);
      setIsFetching(false);
    });
  }, [searchQuery, currentPage]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [currentPage]);

  const totalPages = Math.max(1, Math.ceil(total / itemsPerPage));

  const updateParams = (updater) => {
    const params = new URLSearchParams(searchParams);
    updater(params);
    setSearchParams(params);
  };

  const handleSearchChange = (value) => {
    updateParams((params) => {
      if (value.trim()) params.set("search", value);
      else params.delete("search");
      params.delete("page");
    });
  };

  const handlePageChange = (page) => {
    updateParams((params) => {
      if (page > 1) params.set("page", String(page));
      else params.delete("page");
    });
  };

  return (
    <div className="min-h-screen" data-testid="brands-page">
      <Header />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-8 gap-4">
          <div className="text-left">
            <p className="font-sans text-xs uppercase tracking-[0.2em] text-stone-400 font-semibold mb-2">{t("home.trusted")}</p>
            <h1 className="font-serif text-3xl sm:text-4xl text-stone-900">{t("home.our_brands")}</h1>
            <p className="mt-2 text-sm text-stone-500">{total} {t("common.brands") || "brands"}</p>
          </div>
          <div className="relative w-full sm:w-72">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Search className="h-4 w-4 text-stone-400" />
            </div>
            <Input
              type="text"
              placeholder={t("common.search_brands")}
              value={searchQuery}
              onChange={(e) => handleSearchChange(e.target.value)}
              className="pl-10 h-10 w-full"
            />
          </div>
        </div>

        {loading && brands.length === 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
            {[1,2,3].map((i) => <div key={i} className="h-40 bg-stone-100 animate-pulse" />)}
          </div>
        ) : brands.length === 0 ? (
           <div className="text-center py-20 bg-stone-50 border border-stone-100 rounded">
             <p className="text-stone-500">{t("common.no_brands")}</p>
           </div>
        ) : (
          <div className="relative">
            {isFetching && brands.length > 0 && (
              <div className="absolute top-0 right-0 z-10 rounded-full border border-stone-200 bg-white/90 px-3 py-1 text-xs text-stone-500 shadow-sm backdrop-blur-sm">
                {t("common.loading")}
              </div>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {brands.map((brand) => (
                <Link
                  key={brand.id}
                  to={`/brands/${brand.id}`}
                  className="group bg-white border border-stone-100 p-8 flex flex-col justify-between hover:border-primary/30 transition-all rounded"
                  data-testid={`brand-link-${brand.id}`}
                >
                  <div className="mb-4">
                    <h2 className="font-serif text-2xl text-stone-800 break-words group-hover:text-primary transition-colors mb-2">{brand.name}</h2>
                    <div className="inline-flex items-center gap-1.5 px-2 py-1 bg-stone-50 rounded text-[10px] font-semibold tracking-wide uppercase text-stone-500 mb-4">
                      <Package className="w-3 h-3 text-stone-400" /> {brand.product_count || 0} {t("common.products")}
                    </div>
                    <p className="text-sm text-stone-500 leading-relaxed line-clamp-3">{brand.description}</p>
                  </div>
                </Link>
              ))}
            </div>

            {totalPages > 1 && (
              <div className="flex items-center justify-between border-t border-stone-100 pt-6 mt-12">
                <p className="text-sm text-stone-500">{t("common.page")} {currentPage} {t("common.of")} {totalPages}</p>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handlePageChange(Math.max(1, currentPage - 1))}
                    disabled={currentPage === 1}
                    className="p-2 border border-stone-200 rounded text-stone-500 hover:bg-stone-50 disabled:opacity-50"
                  >
                    <ChevronLeft className="w-5 h-5" />
                  </button>
                  <button
                    onClick={() => handlePageChange(Math.min(totalPages, currentPage + 1))}
                    disabled={currentPage === totalPages}
                    className="p-2 border border-stone-200 rounded text-stone-500 hover:bg-stone-50 disabled:opacity-50"
                  >
                    <ChevronRight className="w-5 h-5" />
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
}
